import { Fragment, useMemo, useState } from "react";
import {
  DocumentArrowDownIcon,
  LockClosedIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { SubscriptionToggle } from "./components/SubscriptionToggle";
import { CATEGORY_BADGES, ID_FROM_AUDIENCE_KEY } from "./constants";
import Spinner from "@/ui-components/spinner";
import clsx from "clsx";
import useAuth from "@/hooks/useAuth";
import DemoBanner from "@/ui-components/DemoBanner";
import { useMailingLists } from "./hooks/useMailingLists";
import { useMailingListSubscriptionCrud } from "./hooks/useMailingListSubscriptionCrud";
import { useRecentSheets } from "./hooks/useRecentSheets";
import { trackContentView, trackFeatureUsage } from "@/utils/gtm";
import ResearchHeadlines from "@/widget-components/research-headlines";
import { WidgetContainer } from "@/layout-components/WidgetContainer";
import { getWidgetById } from "@/utils/widgets";
import type { MailingList } from "./api/types";

const ResearchHeadlinesWidget = getWidgetById("ResearchHeadlines");

const NewsquawkResearch = () => {
  const { authenticated } = useAuth();
  const mailingLists = useMailingLists();
  const recentSheets = useRecentSheets();
  const mailingListSubscriptionCrud = useMailingListSubscriptionCrud();
  const isAnyMutationPending =
    mailingListSubscriptionCrud.subscribeMutation.isPending ||
    mailingListSubscriptionCrud.unsubscribeMutation.isPending;
  const [cbExpanded, setCbExpanded] = useState(false);

  // Match a mailing list to its latest research sheet. Most reports share the
  // same slug on both sides (`label === key`), so try that first. Some don't
  // (e.g. mailing-list "week_ahead" vs research-sheet "week_in_focus"); for
  // those, fall back to the shared category id resolved from the audience key.
  const getLatestSheetForList = (mailingListKey: string) => {
    const sheets = recentSheets.data?.sheets;
    if (!sheets) return undefined;

    const byLabel = sheets.find((sheet) => sheet.label === mailingListKey);
    if (byLabel) return byLabel;

    const categoryId = ID_FROM_AUDIENCE_KEY[mailingListKey];
    if (!categoryId) return undefined;
    return sheets.find((sheet) => sheet.id === categoryId);
  };

  const centralBanksSheets = recentSheets.data?.centralBanksSheets ?? [];

  const sortedMailingLists = useMemo(() => {
    const categoryOrder = { daily: 0, weekly: 1, adhoc: 2, monthly: 3 };
    return [...(mailingLists.data ?? [])].sort((a, b) => {
      const catDiff = categoryOrder[a.type] - categoryOrder[b.type];
      if (catDiff !== 0) return catDiff;
      return a.name.localeCompare(b.name);
    });
  }, [mailingLists.data]);

  const dailyLists = useMemo(
    () => sortedMailingLists.filter((ml) => ml.type === "daily"),
    [sortedMailingLists]
  );
  const otherLists = useMemo(
    () => sortedMailingLists.filter((ml) => ml.type !== "daily"),
    [sortedMailingLists]
  );

  const renderRow = (mailingList: MailingList) => {
    const badge = CATEGORY_BADGES[mailingList.type];
    const latestSheet = getLatestSheetForList(mailingList.key);
    const isCentralBanks = mailingList.key === "central_banks";
    const isDisabled = !authenticated || isAnyMutationPending;

    return (
      <Fragment key={mailingList.key}>
        <tr
          className={clsx(
            "hover:bg-gray-100 dark:hover:bg-gray-700/20 transition-colors",
            isDisabled && "opacity-60"
          )}
        >
          <td className="px-3 py-1">
            <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              {mailingList.name}
            </span>
          </td>
          <td className="px-3 py-1">
            <span
              className={clsx(
                "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium",
                badge.className
              )}
            >
              {badge.label}
            </span>
          </td>
          <td className="px-3 py-1">
            {latestSheet ? (
              authenticated ? (
                <a
                  href={latestSheet.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-squawk-blue hover:text-squawk-blue-light transition-colors whitespace-nowrap"
                  onClick={() =>
                    trackContentView({
                      content_id: latestSheet.id ?? mailingList.key,
                      content_title: mailingList.name,
                      content_type: "research_pdf",
                      content_category: mailingList.type,
                      content_source: mailingList.key,
                      content_widget: "research",
                    })
                  }
                >
                  <span>{latestSheet.eventDate}</span>
                  <DocumentArrowDownIcon className="h-3 w-3" />
                </a>
              ) : (
                <button
                  onClick={() =>
                    toast("Sign up to access research sheets", { icon: "🔒" })
                  }
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>{latestSheet.eventDate}</span>
                  <LockClosedIcon className="h-3 w-3" />
                </button>
              )
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-600">
                —
              </span>
            )}
          </td>
          <td className="px-3 py-1.5 text-right">
            <SubscriptionToggle
              checked={mailingList.subscribed}
              onChange={() => {
                if (authenticated) {
                  if (mailingList.subscribed) {
                    mailingListSubscriptionCrud.unsubscribeMutation.mutate(
                      mailingList.id,
                      {
                        onSuccess: () => {
                          toast.success(
                            `Unsubscribed from ${mailingList.name}`
                          );
                          trackFeatureUsage({
                            feature_name: "research_mailing_list",
                            feature_action: "unsubscribe",
                            feature_detail: mailingList.name,
                          });
                        },
                      }
                    );
                  } else {
                    mailingListSubscriptionCrud.subscribeMutation.mutate(
                      mailingList.id,
                      {
                        onSuccess: () => {
                          toast.success(`Subscribed to ${mailingList.name}`);
                          trackFeatureUsage({
                            feature_name: "research_mailing_list",
                            feature_action: "subscribe",
                            feature_detail: mailingList.name,
                          });
                        },
                      }
                    );
                  }
                }
              }}
              loading={isAnyMutationPending}
              disabled={isDisabled}
            />
          </td>
        </tr>

        {/* Central Banks: collapsible reports */}
        {isCentralBanks && centralBanksSheets.length > 0 && (
          <>
            <tr className="bg-gray-50/50 dark:bg-gray-800/20">
              <td colSpan={4} className="px-3 py-1">
                <button
                  onClick={() => setCbExpanded(!cbExpanded)}
                  className="inline-flex items-center gap-1 text-xs text-squawk-blue hover:text-squawk-blue-light transition-colors"
                >
                  <ChevronRightIcon
                    className={clsx(
                      "h-3 w-3 transition-transform",
                      cbExpanded && "rotate-90"
                    )}
                  />
                  <span>
                    {centralBanksSheets.length} recent reports
                  </span>
                </button>
              </td>
            </tr>
            {cbExpanded &&
              centralBanksSheets.map((sheet) => (
                <tr
                  key={sheet.id}
                  className="bg-gray-50/50 dark:bg-gray-800/20"
                >
                  <td className="px-3 py-1 pl-7" colSpan={2}>
                    {authenticated ? (
                      <a
                        href={sheet.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-squawk-blue hover:text-squawk-blue-light transition-colors"
                        onClick={() =>
                          trackContentView({
                            content_id: sheet.id,
                            content_title: sheet.title,
                            content_type: "research_pdf",
                            content_category: "monthly",
                            content_source: "central_banks",
                            content_widget: "research",
                          })
                        }
                      >
                        <DocumentArrowDownIcon className="h-3 w-3 shrink-0" />
                        <span>{sheet.title}</span>
                      </a>
                    ) : (
                      <button
                        onClick={() =>
                          toast("Sign up to access research sheets", {
                            icon: "🔒",
                          })
                        }
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
                      >
                        <LockClosedIcon className="h-3 w-3 shrink-0" />
                        <span>{sheet.title}</span>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {sheet.uploadDate}
                    </span>
                  </td>
                  <td />
                </tr>
              ))}
          </>
        )}
      </Fragment>
    );
  };

  const renderTable = (
    items: MailingList[],
    options?: { hideMobileHeader?: boolean }
  ) => (
    <table className="w-full">
      <thead>
        <tr
          className={clsx(
            "border-b border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50",
            options?.hideMobileHeader && "hidden lg:table-row"
          )}
        >
          <th className="text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider py-1.5 px-3">
            Subscription
          </th>
          <th className="text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider py-1.5 px-3 w-16">
            Type
          </th>
          <th className="text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider py-1.5 px-3 w-20">
            Latest
          </th>
          <th className="w-12" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
        {items.map(renderRow)}
      </tbody>
    </table>
  );

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-950 transition-colors gap-2 p-2">
      {!authenticated && <DemoBanner message="historical research content" />}

      {/* Subscriptions — unified container, two tables side by side */}
      <div className="shrink-0">
        <div className="border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 rounded-t-md shadow-md dark:shadow-lg dark:shadow-black/20 overflow-hidden transition-colors">
          <div className="bg-gray-100 dark:bg-gray-800/80 text-gray-800 dark:text-white text-2xs px-2 py-0.5 select-none transition-colors">
            Mailing List Subscriptions
          </div>
          {mailingLists.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row">
              <div className="flex-1 min-w-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700/50">
                {renderTable(dailyLists)}
              </div>
              <div className="flex-1 min-w-0">
                {renderTable(otherLists, { hideMobileHeader: true })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Research Headlines */}
      <div className="flex-1 min-h-0">
        {ResearchHeadlinesWidget && (
          <WidgetContainer
            widget={ResearchHeadlinesWidget}
            hideDefaultActions
            onClose={() => {}}
          >
            {(options) => (
              <ResearchHeadlines
                headerOptions={options}
                widgetCompositeId="research-page-headlines"
              />
            )}
          </WidgetContainer>
        )}
      </div>
    </div>
  );
};

export default NewsquawkResearch;
